import React, { useEffect, useMemo, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, AreaChart, Area } from "recharts";

/**
 * Public Provident Fund (PPF) Calculator — React + Tailwind + Recharts
 * - Simulates PPF growth year-by-year using the Indian PPF rules approximation:
 *   • Interest rate is annual (default 7.1%), computed monthly on balance considered after the 5th.
 *   • Interest is credited once per financial year end (after March).
 *   • Contribution can be Monthly (each month) or Yearly (April lump sum).
 *   • Deposit day-of-month controls whether the deposit earns interest for that month (<=5 does, >5 doesn't for that month).
 * - Includes: stats, warnings for > ₹1.5L/year deposits, growth chart, year-wise schedule.
 */

const inr = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2,
});

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function round2(n) {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

function Stat({ label, value, sub }) {
  return (
    <div className="p-4 rounded-2xl border bg-white shadow-sm">
      <div className="text-sm text-gray-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
      {sub ? <div className="text-xs text-gray-500 mt-1">{sub}</div> : null}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="text-sm font-medium text-gray-700 flex items-center gap-2">
        {label}
        {hint && <span className="text-xs font-normal text-gray-500">{hint}</span>}
      </div>
      <div className="mt-2">{children}</div>
    </label>
  );
}

const CONTRIB_TYPES = {
  Monthly: "monthly",
  Yearly: "yearly",
};

function simulatePPF({
  openingBalance,
  contribType, // 'monthly' | 'yearly'
  monthlyContribution,
  yearlyContribution,
  depositDay, // 1..28
  ratePct, // annual
  years, // up to 15 (standard lock-in)
}) {
  const r = ratePct / 100;
  const rMonthly = r / 12;
  const schedule = []; // per-year rows
  let balance = openingBalance;
  let totalDeposit = 0;
  let totalInterest = 0;

  // We model financial years (Apr..Mar). For each year:
  for (let y = 1; y <= years; y++) {
    let depositThisYear = 0;
    let interestThisYear = 0;

    for (let m = 0; m < 12; m++) {
      // Month index 0..11 (0 => April, 11 => March)
      let deposit = 0;
      if (contribType === "monthly") {
        deposit = monthlyContribution;
        // Deposit happens on `depositDay` of each month.
        // Interest for the month is on the lowest balance between the 5th and end of month.
        const baseForInterest = balance + (depositDay <= 5 ? deposit : 0);
        const interest = baseForInterest * rMonthly;
        interestThisYear += interest;
        // After interest calculation window, deposit (if after 5th) still goes in for future months
        balance += deposit; // deposit is added regardless; interest impact handled above
        depositThisYear += deposit;
      } else {
        // Yearly contribution assumed in April (m===0)
        if (m === 0) {
          deposit = yearlyContribution;
          const baseForInterest = balance + (depositDay <= 5 ? deposit : 0);
          const interest = baseForInterest * rMonthly;
          interestThisYear += interest;
          balance += deposit;
          depositThisYear += deposit;
        } else {
          // Other months: no deposit; interest on existing balance
          const baseForInterest = balance; // since no deposit this month
          const interest = baseForInterest * rMonthly;
          interestThisYear += interest;
        }
      }
    }

    // Credit the interest at FY end (March)
    balance += interestThisYear;
    totalInterest += interestThisYear;
    totalDeposit += depositThisYear;

    schedule.push({
      year: y,
      deposit: round2(depositThisYear),
      interest: round2(interestThisYear),
      closing: round2(balance),
    });
  }

  return {
    balance: round2(balance),
    totalDeposit: round2(totalDeposit),
    totalInterest: round2(totalInterest),
    schedule,
  };
}

export default function PPFCalculator() {
  const [contribType, setContribType] = useState(CONTRIB_TYPES.Monthly);
  const [monthlyContribution, setMonthlyContribution] = useState(12500); // 1.5L/year
  const [yearlyContribution, setYearlyContribution] = useState(150000);
  const [depositDay, setDepositDay] = useState(1); // before 5th to maximize
  const [ratePct, setRatePct] = useState(7.1);
  const [years, setYears] = useState(15);
  const [openingBalance, setOpeningBalance] = useState(0);

  // Parse from query string for share-ability
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("t");
    const m = parseFloat(params.get("m"));
    const y = parseFloat(params.get("y"));
    const d = parseInt(params.get("d"));
    const r = parseFloat(params.get("r"));
    const n = parseInt(params.get("n"));
    const ob = parseFloat(params.get("ob"));

    if (t === "yearly" || t === "monthly") setContribType(t);
    if (!Number.isNaN(m)) setMonthlyContribution(clamp(m, 0, 200000));
    if (!Number.isNaN(y)) setYearlyContribution(clamp(y, 0, 200000));
    if (!Number.isNaN(d)) setDepositDay(clamp(d, 1, 28));
    if (!Number.isNaN(r)) setRatePct(clamp(r, 0, 15));
    if (!Number.isNaN(n)) setYears(clamp(n, 1, 50));
    if (!Number.isNaN(ob)) setOpeningBalance(clamp(ob, 0, 1e9));
  }, []);

  const results = useMemo(() => {
    return simulatePPF({
      openingBalance,
      contribType,
      monthlyContribution,
      yearlyContribution,
      depositDay,
      ratePct,
      years,
    });
  }, [openingBalance, contribType, monthlyContribution, yearlyContribution, depositDay, ratePct, years]);

  const perYearCap = 150000;
  const plannedYearly = contribType === "monthly" ? monthlyContribution * 12 : yearlyContribution;
  const overCap = plannedYearly > perYearCap + 1e-6;

  const share = () => {
    const url = new URL(window.location.href);
    url.searchParams.set("t", contribType);
    url.searchParams.set("m", String(monthlyContribution));
    url.searchParams.set("y", String(yearlyContribution));
    url.searchParams.set("d", String(depositDay));
    url.searchParams.set("r", String(ratePct));
    url.searchParams.set("n", String(years));
    url.searchParams.set("ob", String(openingBalance));
    navigator.clipboard.writeText(url.toString());
    alert("Sharable link copied to clipboard!");
  };

  const reset = () => {
    setContribType(CONTRIB_TYPES.Monthly);
    setMonthlyContribution(12500);
    setYearlyContribution(150000);
    setDepositDay(1);
    setRatePct(7.1);
    setYears(15);
    setOpeningBalance(0);
  };

  // Chart data (year -> closing)
  const chartData = results.schedule.map((row) => ({
    label: `Y${row.year}`,
    value: row.closing,
  }));

  return (
    <div className="min-h-screen w-full bg-gray-50 text-gray-900">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <header className="mb-6 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">PPF Calculator (India)</h1>
            <p className="text-gray-600 mt-1">Estimate your PPF maturity with monthly/annual deposits and realistic interest crediting.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={share} className="px-3 py-2 rounded-xl border bg-white hover:bg-gray-100 text-sm">Share</button>
            <button onClick={reset} className="px-3 py-2 rounded-xl border bg-white hover:bg-gray-100 text-sm">Reset</button>
          </div>
        </header>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left: Form */}
          <div className="lg:col-span-1 p-5 rounded-2xl border bg-white shadow-sm">
            <div className="space-y-5">
              <Field label="Contribution Type">
                <div className="flex items-center gap-4 text-sm">
                  {Object.entries(CONTRIB_TYPES).map(([label, val]) => (
                    <label key={val} className="inline-flex items-center gap-2">
                      <input
                        type="radio"
                        name="ct"
                        value={val}
                        checked={contribType === val}
                        onChange={(e) => setContribType(e.target.value)}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </Field>

              {contribType === "monthly" ? (
                <Field label="Monthly Contribution">
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step={500}
                    value={monthlyContribution}
                    onChange={(e) => setMonthlyContribution(clamp(parseFloat(e.target.value || "0"), 0, 200000))}
                    className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="text-xs text-gray-500 mt-1">Per year: {inr.format(monthlyContribution * 12)}</div>
                </Field>
              ) : (
                <Field label="Yearly Contribution" hint="assumed in April">
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step={1000}
                    value={yearlyContribution}
                    onChange={(e) => setYearlyContribution(clamp(parseFloat(e.target.value || "0"), 0, 200000))}
                    className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </Field>
              )}

              <Field label="Deposit Day of Month" hint="≤ 5 earns interest for that month">
                <input
                  type="number"
                  min={1}
                  max={28}
                  value={depositDay}
                  onChange={(e) => setDepositDay(clamp(parseInt(e.target.value || "1"), 1, 28))}
                  className="w-32 rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </Field>

              <Field label="Annual Interest Rate" hint="% p.a.">
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    max={15}
                    step={0.05}
                    value={ratePct}
                    onChange={(e) => setRatePct(clamp(parseFloat(e.target.value || "0"), 0, 15))}
                    className="w-40 rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="range"
                    min={0}
                    max={10}
                    step={0.05}
                    value={clamp(ratePct, 0, 10)}
                    onChange={(e) => setRatePct(clamp(parseFloat(e.target.value || "0"), 0, 15))}
                    className="flex-1"
                  />
                </div>
              </Field>

              <Field label="Tenure (years)" hint="PPF lock-in is 15 years">
                <input
                  type="number"
                  min={1}
                  max={50}
                  step={1}
                  value={years}
                  onChange={(e) => setYears(clamp(parseInt(e.target.value || "1"), 1, 50))}
                  className="w-32 rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </Field>

              <Field label="Opening Balance" hint="existing PPF corpus">
                <input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step={1000}
                  value={openingBalance}
                  onChange={(e) => setOpeningBalance(clamp(parseFloat(e.target.value || "0"), 0, 1e12))}
                  className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </Field>

              {overCap && (
                <div className="p-3 rounded-xl border bg-amber-50 text-amber-900 text-sm">
                  Planned yearly deposit {inr.format(plannedYearly)} exceeds the current legal cap of {inr.format(perYearCap)} per financial year. Adjust your contribution to comply.
                </div>
              )}

              <div className="text-xs text-gray-500 leading-relaxed">
                <p>Interest is computed monthly (on balance as of the 5th) and credited annually at FY end. Real-world payouts may vary slightly by bank rounding.</p>
              </div>
            </div>
          </div>

          {/* Right: Summary + Chart + Table */}
          <div className="lg:col-span-2 space-y-6">
            <div className="grid md:grid-cols-3 gap-4">
              <Stat label="Maturity Value" value={inr.format(results.balance)} sub={`${years} years`} />
              <Stat label="Total Deposit" value={inr.format(results.totalDeposit)} sub={contribType === "monthly" ? `${inr.format(monthlyContribution)} / mo` : `${inr.format(yearlyContribution)} / yr`} />
              <Stat label="Total Interest" value={inr.format(results.totalInterest)} sub={`Rate ${ratePct}% p.a.`} />
            </div>

            <div className="p-5 rounded-2xl border bg-white shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold">Corpus Growth (Yearly)</h2>
                <div className="text-sm text-gray-600">Closing balance at FY end</div>
              </div>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={(v) => inr.format(v).replace("₹", "₹ ")} tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(v) => inr.format(v)} labelFormatter={(l) => `Year: ${l}`} />
                    <Area type="monotone" dataKey="value" strokeWidth={3} dot={false} fillOpacity={0.15} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="p-5 rounded-2xl border bg-white shadow-sm">
              <h3 className="text-base font-semibold mb-3">Year-wise Schedule</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-600 border-b">
                      <th className="py-2 pr-4">Year</th>
                      <th className="py-2 pr-4">Deposit</th>
                      <th className="py-2 pr-4">Interest</th>
                      <th className="py-2 pr-4">Closing Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.schedule.map((row) => (
                      <tr key={row.year} className="border-b last:border-b-0">
                        <td className="py-2 pr-4">{row.year}</td>
                        <td className="py-2 pr-4">{inr.format(row.deposit)}</td>
                        <td className="py-2 pr-4">{inr.format(row.interest)}</td>
                        <td className="py-2 pr-4 font-medium">{inr.format(row.closing)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="p-5 rounded-2xl border bg-white shadow-sm">
              <h3 className="text-base font-semibold mb-2">Assumptions & Notes</h3>
              <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                <li>Interest is computed monthly using the balance considered after the 5th of each month, and credited annually (at March end).</li>
                <li>For monthly contributions, setting deposit day ≤ 5 maximizes interest (counts from the same month).</li>
                <li>Legal deposit cap: ₹1.5 lakh per financial year (subject to change); calculator flags if you exceed the cap.</li>
                <li>Government-notified PPF interest rate can change quarterly; adjust the % to model scenarios.</li>
                <li>Tax treatment: PPF is generally EEE (contributions eligible u/s 80C, interest and maturity tax-free). Consult a tax advisor for specifics.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
